import { isValidElement, ReactNode } from 'react';

import { CalendarDate } from '@internationalized/date';

import { toCamelCase } from './string';
import { PojoT, TestDateT } from '../types';

export const isObject = (value: unknown): value is Record<PropertyKey, unknown> => {
  return value !== null && typeof value === 'object';
};

export type CamelizeKeysOptionsT = {
  deep?: boolean;
};

/**
 * Camelize object keys. Returns a new object.
 *
 * options:
 *   deep?: boolean - recursively camelize all child object keys
 */
export const camelizeKeys = <T extends PojoT, T2 = PojoT>(obj: T, options?: CamelizeKeysOptionsT): T2 => {
  if (!Object.keys(obj).length) { return obj as unknown as T2; }
  if (Array.isArray(obj)) { return obj as unknown as T2; }

  return Object
    .entries(obj)
    .reduce(
      (acc, [key, value]) => {
        (acc as PojoT)[toCamelCase(key)] = options?.deep && isObject(value) ? camelizeKeys(value, options) : value;

        return acc;
      },
      {} as T2,
    )
  ;
};

export const getReactNodeText = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getReactNodeText).join('');
  }

  if (isValidElement<{ children?: ReactNode; }>(node)) {
    return getReactNodeText(node.props.children);
  }

  return '';
};

// Fixes a circular dependency bug
export const safeReplacer = (_key: string, value: unknown): unknown | TestDateT => {
  if (value instanceof CalendarDate) {
    const {
      day,
      month,
      year,
    } = value;

    return {
      day,
      month,
      year,
    } satisfies TestDateT;
  }

  return value;
};

export const isEmptyObject = (value: unknown): value is Record<PropertyKey, never> => {
  return (
    isObject(value)
    && Object.keys(value).length === 0
  );
};

/**
 * Compare two objects to determine if they are equal. Recursively compares child objects.
 */
export const deepEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) {
    return true;
  }

  if (!isObject(a) || !isObject(b)) {
    return false;
  }

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  return aKeys.every((key: string): boolean => {
    if (!Object.prototype.hasOwnProperty.call(b, key)) {
      return false;
    }

    return deepEqual(a[key], b[key]);
  });
};

/**
 * Compare two objects to determine if one object contains some of the values of the other.
 */
export const objectContains = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) {
    return true;
  }

  if (!isObject(a) || !isObject(b)) {
    return a === b;
  }

  const bKeys = Object.keys(b);

  return bKeys.every((key: string): boolean => {
    if (!Object.prototype.hasOwnProperty.call(a, key)) {
      return false;
    }

    return objectContains(a[key], b[key]);
  });
};
